import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { DiagnosticVerdict, ProductDiagnostic } from '../api/types';
import { Portal } from './Portal';

interface ProductDiagnosticModalProps {
  storeId: string;
  productId: string;
  onClose: () => void;
  onToast?: (msg: string) => void;
}

const VERDICT_LABEL: Record<DiagnosticVerdict, string> = {
  current: 'CURRENT — the live ad matches the source',
  stale_price: 'STALE PRICE — the ad shows an out-of-date price',
  stale_image: 'STALE IMAGE — the source photo changed since this render',
  not_rendered: 'NOT RENDERED — no current render for this product',
  not_in_source: 'NOT IN SOURCE — delisted or out of stock',
};

const VERDICT_TONE: Record<DiagnosticVerdict, string> = {
  current: 'bg-ff-mint/30 text-ff-forest border-ff-forest/30',
  stale_price: 'bg-ff-danger-soft text-ff-danger border-ff-danger/30',
  stale_image: 'bg-ff-danger-soft text-ff-danger border-ff-danger/30',
  not_rendered: 'bg-ff-sand/40 text-ff-ink border-ff-sand',
  not_in_source: 'bg-ff-sand/40 text-ff-ink border-ff-sand',
};

function Cell({ value, differs, href }: {
  value: string | null | undefined;
  differs?: boolean;
  href?: string | null;
}) {
  const cls = `py-1 pr-3 text-sm break-all align-top ${differs ? 'text-ff-danger font-semibold' : 'text-ff-ink'}`;
  if (value && href) {
    return (
      <td className={cls}>
        <a className="underline text-ff-forest" href={href} target="_blank" rel="noreferrer">
          {value}
        </a>
      </td>
    );
  }
  return <td className={cls}>{value ?? '—'}</td>;
}

function Row({ label, source, render, differs, sourceHref, renderHref }: {
  label: string;
  source: string | null | undefined;
  render: string | null | undefined;
  differs?: boolean;
  sourceHref?: string | null;
  renderHref?: string | null;
}) {
  return (
    <tr className="border-b border-ff-sand/40 last:border-0">
      <td className="py-1 pr-3 font-mono text-[11px] uppercase tracking-widish text-ff-mute whitespace-nowrap align-top">
        {label}
      </td>
      <Cell value={source} differs={differs} href={sourceHref} />
      <Cell value={render} differs={differs} href={renderHref} />
    </tr>
  );
}

const numEq = (a: string | null | undefined, b: string | null | undefined) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  return Number.isNaN(na) || Number.isNaN(nb) ? a === b : na === nb;
};

export function ProductDiagnosticModal({
  storeId, productId, onClose, onToast,
}: ProductDiagnosticModalProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const diagQ = useQuery<ProductDiagnostic, Error>({
    queryKey: ['product-diagnostic', storeId, productId],
    queryFn: () => api.getProductDiagnostic(storeId, productId),
    retry: false,
  });

  const rerunM = useMutation({
    mutationFn: () => api.rerunProduct(storeId, productId),
    onSuccess: (res) => {
      queryClient.setQueryData(['product-diagnostic', storeId, productId], res.diagnostic);
      const s = res.stats;
      const what = s.rendered > 0
        ? 'rendered fresh'
        : s.pointer_repaired > 0
          ? 'already rendered — repaired the stale pointer'
          : 'already current (cache hit)';
      onToast?.(`Re-run done: ${what}.`);
    },
    onError: (e) => {
      onToast?.(`Re-run failed: ${e instanceof HttpError ? e.detail : e.message}`);
    },
  });

  const d = diagQ.data;
  const err = diagQ.error;

  return (
    <Portal>
      {/* z-[60]: opens from inside a card and must stack above the z-50 zoom
          modal + z-30 sticky toolbars (backdrop-blur stacking precedent). Width derives from
          the viewport so the comparison table never clips (the 3xl fixed width
          cut off the rendered-ad column on laptop screens). */}
      <div
        className="fixed inset-0 z-[60] bg-ff-ink/80 flex items-center justify-center p-4 md:p-8"
        onClick={onClose}
        data-testid="diagnostic-modal"
      >
        <div
          className="bg-ff-ivory rounded-xl w-[min(96vw,80rem)] max-h-[92vh] overflow-y-auto overflow-x-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Product diagnostic for ${productId}`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-ff-sand/60">
            <div className="flex items-baseline gap-3">
              <div className="font-display text-xl text-ff-forest">Diagnose</div>
              <div className="font-mono text-xs text-ff-mute">{productId}</div>
            </div>
            <button onClick={onClose} type="button" className="ff-btn-ghost">
              Close (Esc)
            </button>
          </div>

          {diagQ.isPending && (
            <div className="p-6 text-sm text-ff-mute">Loading diagnostic…</div>
          )}
          {err && (
            <div className="p-6 text-sm text-ff-danger">
              {err instanceof HttpError && err.status === 404
                ? 'Product not found in the source or in renders.'
                : `Failed to load: ${err instanceof HttpError ? err.detail : err.message}`}
            </div>
          )}

          {d && (
            <div className="p-6 space-y-5">
              <div
                className={`rounded-lg border px-4 py-2 text-sm font-semibold ${VERDICT_TONE[d.verdict]}`}
                data-testid="diagnostic-verdict"
              >
                {VERDICT_LABEL[d.verdict]}
              </div>
              {d.differences.length > 0 && (
                <ul className="text-xs text-ff-mute list-disc pl-5 space-y-0.5">
                  {d.differences.map((x) => <li key={x}>{x}</li>)}
                </ul>
              )}

              {/* table-fixed + break-all: long CDN URLs wrap inside their
                  columns instead of pushing the table past the frame. */}
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-28" />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr className="text-left border-b border-ff-sand">
                    <th className="py-1 pr-3" />
                    <th className="py-1 pr-3 font-mono text-[11px] uppercase tracking-widish text-ff-mute">
                      Source (latest fetch)
                    </th>
                    <th className="py-1 font-mono text-[11px] uppercase tracking-widish text-ff-mute">
                      Rendered ad
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <Row label="Title" source={d.source?.title} render={d.render?.title} />
                  <Row
                    label="Price"
                    source={d.source?.price}
                    render={d.render?.price_sek}
                    differs={!numEq(d.source?.price, d.render?.price_sek)}
                  />
                  <Row
                    label="Sale price"
                    source={d.source?.sale_price}
                    render={d.render?.sale_price_sek}
                    differs={!numEq(d.source?.sale_price, d.render?.sale_price_sek)}
                  />
                  <Row
                    label="Product URL"
                    source={d.source?.link}
                    render="—"
                    sourceHref={d.source?.link}
                  />
                  <Row
                    label="Photo"
                    source={d.source?.image_url}
                    render={d.render?.cutout_source_image_url}
                    sourceHref={d.source?.image_url}
                    renderHref={d.render?.cutout_source_image_url}
                    differs={
                      !d.state.pinned
                      && d.source != null
                      && d.render != null
                      && d.source.image_url !== d.render.cutout_source_image_url
                    }
                  />
                  <Row label="Rendered at" source="—" render={d.render?.rendered_at} />
                  <Row label="Config" source="—" render={d.render?.config_name} />
                </tbody>
              </table>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-1">
                    Current ad
                  </div>
                  {d.render ? (
                    <a href={d.render.adjusted_image_link} target="_blank" rel="noreferrer">
                      <img
                        src={d.render.adjusted_image_link}
                        alt="Current ad"
                        className="w-full max-w-md bg-ff-sand/20 rounded"
                      />
                    </a>
                  ) : (
                    <div className="text-xs text-ff-mute">no render</div>
                  )}
                </div>
                <div className="space-y-2 text-xs">
                  <div className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
                    Cutout
                  </div>
                  {d.cutout?.url ? (
                    <div>
                      <a
                        className="underline break-all text-ff-forest"
                        href={d.cutout.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {d.cutout.url.split('/').slice(-1)[0]}
                      </a>
                      <div className="text-ff-mute mt-0.5">
                        {d.cutout.kind === 'pinned' ? 'pinned object' : 'derived from tier'}
                        {' · '}tier {d.cutout.tier ?? '—'}
                        {' · '}
                        {d.cutout.exists === true
                          ? 'exists in GCS'
                          : d.cutout.exists === false
                            ? 'MISSING in GCS'
                            : 'existence unknown'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-ff-mute">no cutout object derivable</div>
                  )}
                  <div className="font-mono text-[11px] uppercase tracking-widish text-ff-mute pt-2">
                    State
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {d.state.pinned && (
                      <span className="px-1.5 py-0.5 rounded bg-ff-mint/40 text-ff-forest">
                        pinned {d.state.approved_tier ?? ''}
                      </span>
                    )}
                    {d.state.override_tier && (
                      <span className="px-1.5 py-0.5 rounded bg-ff-sand/50">
                        override → {d.state.override_tier}
                      </span>
                    )}
                    {d.state.qa_verdict && (
                      <span className="px-1.5 py-0.5 rounded bg-ff-sand/50">
                        QA {d.state.qa_verdict} ({d.state.qa_reviewer ?? '—'})
                      </span>
                    )}
                    {!d.state.pinned && !d.state.override_tier && !d.state.qa_verdict && (
                      <span className="text-ff-mute">no pin, no override, no verdict</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-ff-sand/60">
                <button
                  type="button"
                  className="ff-btn-primary disabled:opacity-50"
                  disabled={rerunM.isPending || d.verdict === 'not_in_source'}
                  onClick={() => rerunM.mutate()}
                  data-testid="diagnostic-rerun"
                >
                  {rerunM.isPending ? 'Re-running… (~1 min)' : 'Re-run this product'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
